import {
  CandidateApprovalStatus,
  CandidateAudience,
  CandidateCtaType,
  CandidateReadinessIssue,
  CandidateRenderSlot,
  CohortAttribute,
  CohortErrorCode,
  CohortMode,
  CsvRejectionReason,
  EditionActionErrorCode,
  EditionAudience,
  EditionStatus,
  FeatureCatalogStatus,
  FeatureCategory,
  MemberAddRejectionReason,
  MemberOrigin,
  MemberStatus,
  RecipientCohortPurpose,
} from './types';

export const EDITIONS_QUERY_KEY = 'digestEditions';
export const EDITION_QUERY_KEY = 'digestEdition';
export const CANDIDATES_QUERY_KEY = 'digestEditionCandidates';
export const CADENCE_QUERY_KEY = 'digestCadenceConfig';
export const EDITION_RECIPIENTS_SUMMARY_QUERY_KEY = 'digestEditionRecipientsSummary';
export const FEATURE_CATALOG_QUERY_KEY = 'featureCatalogEntries';

export const EDITION_STATUS_LABEL: Record<EditionStatus, string> = {
  draft: 'Borrador',
  pending_approval: 'Pendiente',
  approved: 'Aprobada',
  sent: 'Enviada',
  archived: 'Archivada',
};

export const EDITION_STATUS_CLASSES: Record<EditionStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  pending_approval: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  sent: 'bg-green-100 text-green-800',
  archived: 'bg-gray-100 text-gray-400',
};

export const CANDIDATE_STATUS_LABEL: Record<CandidateApprovalStatus, string> = {
  pending: 'Pendiente',
  approved: 'Aprobado',
  rejected: 'Rechazado',
};

export const CANDIDATE_STATUS_CLASSES: Record<CandidateApprovalStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-700',
};

export const READINESS_ISSUE_LABEL: Record<CandidateReadinessIssue, string> = {
  missing_category: 'Sin categoría',
  missing_cta_label: 'Botón sin texto (se usará «Ver más»)',
  missing_cta_destination: 'Sin link: no se mostrará el botón',
  not_approved: 'Falta aprobar',
  support_content_not_verified: 'Soporte no verificó el contenido',
  feature_not_active: 'Feature Legacy o Descontinuado: no saldrá en el correo',
};

export const FEATURE_STATUS_LABEL: Record<FeatureCatalogStatus, string> = {
  active: 'Activo',
  legacy: 'Legacy',
  discontinued: 'Descontinuado',
};

export const FEATURE_NOT_ACTIVE_ADD_ERROR =
  'Este feature ya no está vigente (Legacy o Descontinuado) y no se puede agregar a una edición.';

export const LEARN_MORE_MISSING_DESTINATION_LABEL = 'Sin link: no se mostrará en «Conoce más»';

export const RENDER_SLOT_LABEL: Record<CandidateRenderSlot, string> = {
  hero: 'Hero (portada)',
  card: 'Card (estándar)',
  learn_more: 'Learn more (fondo)',
};

export const RENDER_SLOT_OPTIONS: { value: CandidateRenderSlot; label: string }[] = [
  { value: 'hero', label: RENDER_SLOT_LABEL.hero },
  { value: 'card', label: RENDER_SLOT_LABEL.card },
  { value: 'learn_more', label: RENDER_SLOT_LABEL.learn_more },
];

export const CTA_TYPE_LABEL: Record<CandidateCtaType, string> = {
  lead_capture: 'Lead capture (formulario → CS)',
  self_serve: 'Self-serve (link directo)',
};

export const CTA_TYPE_OPTIONS: { value: CandidateCtaType; label: string }[] = [
  { value: 'lead_capture', label: CTA_TYPE_LABEL.lead_capture },
  { value: 'self_serve', label: CTA_TYPE_LABEL.self_serve },
];

export const CATEGORY_LABEL: Record<FeatureCategory, string> = {
  fila_digital: 'Fila Digital',
  telefonia: 'Telefonía',
  coordinacion_citas: 'Coordinación de Citas',
  agentes_ia: 'Agentes IA',
  integrations: 'Integraciones',
  tareas: 'Tareas',
  herramientas_brainhi: 'Herramientas de BrainHi',
  citas_pr: 'Citas.pr',
  mensajeria: 'Mensajería (Inbox)',
};

export const CATEGORY_COLOR: Record<FeatureCategory, string> = {
  fila_digital: '#1E40AF',
  telefonia: '#065F46',
  coordinacion_citas: '#92400E',
  agentes_ia: '#5B21B6',
  integrations: '#9D174D',
  tareas: '#0369A1',
  herramientas_brainhi: '#374151',
  citas_pr: '#B45309',
  mensajeria: '#4D7C0F',
};

export const NO_CATEGORY_LABEL = 'Sin área';

export const CATEGORY_OPTIONS: { value: FeatureCategory; label: string }[] = (
  Object.keys(CATEGORY_LABEL) as FeatureCategory[]
).map((value) => ({ value, label: CATEGORY_LABEL[value] }));

export const AUDIENCE_LABEL: Record<CandidateAudience, string> = {
  all: 'Todos',
  enterprise: 'Solo Enterprise',
  smb: 'Solo SMB',
};

export const AUDIENCE_OPTIONS: { value: CandidateAudience; label: string }[] = [
  { value: 'all', label: AUDIENCE_LABEL.all },
  { value: 'enterprise', label: AUDIENCE_LABEL.enterprise },
  { value: 'smb', label: AUDIENCE_LABEL.smb },
];

// Editions served before the backend returned `audience` are Staff editions.
export const DEFAULT_EDITION_AUDIENCE: EditionAudience = 'staff';

export const EDITION_AUDIENCE_LABEL: Record<EditionAudience, string> = {
  staff: 'Staff',
  admin: 'Admins',
};

export const EDITION_AUDIENCE_OPTIONS: { id: EditionAudience; label: string }[] = (
  Object.keys(EDITION_AUDIENCE_LABEL) as EditionAudience[]
).map((id) => ({ id, label: EDITION_AUDIENCE_LABEL[id] }));

export const RENDER_SLOT_ORDER: CandidateRenderSlot[] = ['hero', 'card', 'learn_more'];

export const COHORT_ATTRIBUTE_LABEL: Record<CohortAttribute, string> = {
  waitinglist_v2: 'En lista de espera v2',
  has_soft_phone: 'Tiene softphone',
  agent_mode_enabled: 'Modo agente activo',
  has_telemedicine_setup: 'Tiene telemedicina',
  can_use_whatsapp: 'Puede usar WhatsApp',
};

export const COHORT_ATTRIBUTE_OPTIONS: { value: CohortAttribute; label: string }[] = (
  Object.keys(COHORT_ATTRIBUTE_LABEL) as CohortAttribute[]
).map((value) => ({ value, label: COHORT_ATTRIBUTE_LABEL[value] }));

export const EDITION_ACTION_ERROR_MESSAGE: Record<EditionActionErrorCode, string> = {
  edition_wrong_status:
    'La edición ya no está en un estado en el que se pueda hacer este cambio. Recarga la página para ver su estado.',
  edition_candidates_not_ready:
    'Hay candidatos sin aprobar. Aprueba todos los candidatos (con el contenido verificado) o recházalos antes de marcarla lista.',
  edition_feature_not_active:
    'Hay candidatos cuyo feature ya no está vigente (Legacy o Descontinuado). Recházalos antes de marcarla lista.',
  edition_cohort_not_set:
    'Faltan las cuentas. Elige un cohort de cuentas y pulsa "Aplicar cuentas" antes de marcarla lista.',
  edition_cohort_empty:
    'El cohort de cuentas no tiene cuentas: revisa su configuración o elige otro.',
  edition_cohort_no_members:
    'Nadie recibiría esta edición: ninguna persona activa del cohort de personas trabaja en las cuentas elegidas. Cambia el cohort de cuentas antes de marcarla lista.',
  edition_missing_send_date: 'Falta la fecha de envío. Elige una fecha antes de marcarla lista.',
  edition_missing_titulo: 'Falta el título (asunto). Escríbelo y guarda antes de marcarla lista.',
  edition_too_many_heroes:
    'Hay más de 2 candidatos como Hero. Cambia alguno a Card o Learn more antes de marcarla lista.',
  edition_self_serve_missing_cta_url:
    'Hay candidatos self-serve sin URL del CTA. Complétala en cada uno antes de marcarla lista.',
  edition_not_sent: 'Esta edición todavía no se ha enviado, así que no hay envíos que reintentar.',
};

export const RECIPIENT_COHORTS_QUERY_KEY = 'recipientCohorts';
export const RECIPIENT_COHORT_MEMBERS_QUERY_KEY = 'recipientCohortMembers';
export const RECIPIENT_COHORT_USER_SEARCH_QUERY_KEY = 'recipientCohortUserSearch';
export const ACCOUNT_COHORTS_QUERY_KEY = 'accountCohorts';
export const ACCOUNT_COHORT_PREVIEW_QUERY_KEY = 'accountCohortPreview';
export const ACCOUNT_COHORT_ACTIVE_ACCOUNTS_QUERY_KEY = 'accountCohortActiveAccounts';

export const COHORT_MEMBERS_PAGE_SIZE = 20;
export const COHORT_SEARCH_MIN_LENGTH = 2;
export const CSV_REJECTED_PREVIEW_LIMIT = 100;

export const RECIPIENT_COHORT_PURPOSE_LABEL: Record<RecipientCohortPurpose, string> = {
  marketing: 'Marketing',
  feature_release: 'Lanzamientos de features',
};

export const RECIPIENT_COHORT_PURPOSE_OPTIONS: { value: RecipientCohortPurpose; label: string }[] =
  (Object.keys(RECIPIENT_COHORT_PURPOSE_LABEL) as RecipientCohortPurpose[]).map((value) => ({
    value,
    label: RECIPIENT_COHORT_PURPOSE_LABEL[value],
  }));

// The send list a purpose should use unless the user picks another one.
export const DEFAULT_SEND_LIST_BY_PURPOSE: Record<RecipientCohortPurpose, EditionAudience> = {
  marketing: 'admin',
  feature_release: 'staff',
};

export const MEMBER_STATUS_LABEL: Record<MemberStatus, string> = {
  active: 'Activas',
  unsubscribed: 'Dadas de baja',
  removed: 'Quitadas',
};

export const MEMBER_STATUS_CLASSES: Record<MemberStatus, string> = {
  active: 'bg-green-100 text-green-800',
  unsubscribed: 'bg-yellow-100 text-yellow-800',
  removed: 'bg-gray-100 text-gray-500',
};

export const MEMBER_ORIGIN_LABEL: Record<MemberOrigin, string> = {
  role: 'Por rol',
  manual: 'Manual',
  csv: 'CSV',
};

export const MEMBER_ADD_REJECTION_LABEL: Record<MemberAddRejectionReason, string> = {
  unsubscribed: 'Dada de baja',
  not_eligible: 'No es elegible para recibir emails',
  already_member: 'Ya es miembro',
};

export const CSV_REJECTION_REASON_LABEL: Record<CsvRejectionReason, string> = {
  invalid_email: 'Email inválido',
  duplicate_in_file: 'Repetido en el archivo',
  no_user: 'No existe un usuario activo con ese email',
  ambiguous_email: 'Más de un usuario con ese email',
  unsubscribed: 'Dado de baja',
  empty: 'Fila vacía',
};

export const COHORT_ERROR_MESSAGE: Record<CohortErrorCode, string> = {
  cohort_name_taken: 'Ya existe un cohort con ese nombre',
  cohort_has_scheduled_edition:
    'No se puede archivar: el cohort está en una edición aprobada o con fecha de envío. Cambia el cohort de esa edición primero.',
  base_cohort_protected: 'Staff y Admins son cohorts base: no se pueden editar ni archivar.',
  base_cohort_members_read_only:
    'Los miembros de Staff y Admins se calculan por rol y no se pueden editar.',
  user_unsubscribed:
    'Esa persona se dio de baja de la lista y no se puede añadir. Respeta su baja.',
  csv_too_large: 'El archivo es demasiado grande. El máximo es 1 MB y 5000 filas.',
  csv_invalid: 'El archivo no se pudo leer. Revisa que sea un CSV válido.',
  membership_not_found: 'Esa persona ya no es miembro activa de este cohort.',
  account_cohort_protected:
    'Este cohort de cuentas es protegido: solo se puede editar su descripción.',
  account_cohort_in_use:
    'Una edición aprobada usa este cohort de cuentas, así que no se puede cambiar ni archivar. Cancela el envío de esa edición primero.',
  cohort_has_active_edition:
    'Este cohort ya tiene una edición en borrador o aprobada. Envíala o archívala antes de crear otra.',
  cohort_archived: 'Este cohort está archivado y no se puede usar en una edición. Elige otro.',
  cohort_send_list_locked:
    'La lista de bajas no se puede cambiar porque este cohort ya tiene ediciones.',
};

export const ACCOUNT_COHORT_MODE_LABEL: Record<CohortMode, string> = {
  all: 'Todas las cuentas activas',
  automatic: 'Por atributo',
  manual: 'Manual',
};
